import React from 'react';
import { motion } from 'framer-motion';
import { Shield, Lock, CreditCard, BookOpen, Zap, Star } from 'lucide-react';
import { CTAButton } from './Shared';
import { FeatherQuill, Flame, MoonPhase, LotusBud } from './HealingIcons';
const heroImage = {
  src: 'https://cdn.magicpatterns.com/uploads/nQxQLxwSm8RtPsiGeoETSs/ChatGPT_Image_12_ago_2026,_11_42_22.png',
  alt: 'Kit Saberes Ancestrales Latinoamérica — sistema clínico completo'
};
export const Hero = () => {
  return <div className="relative overflow-hidden pb-8 md:pt-20 md:pb-12 bg-transparent pt-[10px]" data-id="element-146" data-mp="xae8p3xwgomv">
      {/* Background Glow */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-brand-purpleLight/10 rounded-full blur-[140px] -z-10 pointer-events-none" data-id="element-147" data-mp="sai4b3laiv6s"></div>

      {/* Floating Decor */}
      <FeatherQuill className="hidden md:block absolute top-32 left-[6%] w-8 h-8 opacity-40 animate-float-soft -z-10" color="#2F6B34" style={{
      transform: 'rotate(20deg)'
    }} data-id="element-148" data-mp="3d2l8r9xwn0zn" />
      <MoonPhase className="hidden md:block absolute top-48 right-[8%] w-10 h-10 opacity-40 animate-float-soft -z-10" color="#A6D93B" style={{
      transform: 'rotate(-25deg)',
      animationDelay: '1.2s'
    }} data-id="element-149" data-mp="3j89618jhw3nf" />
      <LotusBud className="hidden md:block absolute bottom-40 left-[4%] w-12 h-12 opacity-40 animate-float-soft -z-10" color="#C9A961" style={{
      animationDelay: '2s'
    }} data-id="element-150" data-mp="399bctsb4jly3" />
      <Flame className="hidden md:block absolute bottom-32 right-[5%] w-8 h-8 opacity-50 animate-float-soft animate-ember -z-10" color="#A88838" style={{
      transform: 'rotate(15deg)',
      animationDelay: '0.6s'
    }} data-id="element-151" data-mp="30hrgtdxvfq6t" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-152" data-mp="2ql6e4z9bqr65">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="bg-transparent space-y-[0px]" data-id="element-153" data-mp="2vi55ja1lsk0n">
          <div className="inline-flex items-center justify-center bg-white/80 backdrop-blur-sm border border-brand-purple/30 px-4 py-1.5 rounded-full mb-6 gap-2 shadow-sm pt-[2px] pb-[2px] pl-[10px] pr-[10px]" data-id="element-154" data-mp="2ljgy1578vnb7">
            <span className="text-sm" data-id="element-155" data-mp="n9qjjcaquy90">🌿</span>
            <span className="font-serif font-semibold tracking-wider text-xs md:text-sm text-brand-purpleDark" data-id="element-156" data-mp="ibyqgeyd5638">
              LUMEN AKASHA™
            </span>
          </div>

          <div className="relative inline-block mb-4 max-w-4xl mx-auto" data-id="element-157" data-mp="db2f5gbomhns">
            {/* Ambient glow behind headline */}
            <div className="absolute inset-0 -z-10 blur-3xl opacity-60 pointer-events-none" data-id="element-158" data-mp="1622irq63pjex">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[110%] h-[140%] bg-gradient-to-r from-brand-purpleLight/30 via-brand-gold/20 to-brand-purple/30 rounded-full" data-id="element-159" data-mp="112pv537eo8ba" />
            </div>

            {/* Sparkles */}
            <span className="sparkle absolute -top-2 left-[8%] text-brand-purple text-sm md:text-xl" style={{
            animationDelay: '0s'
          }} data-id="element-160" data-mp="3ltqgvowuphzb">
              ✦
            </span>
            <span className="sparkle absolute top-1 right-[6%] text-brand-gold text-xs md:text-lg" style={{
            animationDelay: '0.8s'
          }} data-id="element-161" data-mp="1xam6asypfuzz">
              ✧
            </span>
            <span className="sparkle absolute -bottom-1 left-[20%] text-brand-purpleLight text-xs md:text-base" style={{
            animationDelay: '1.6s'
          }} data-id="element-162" data-mp="268y5hjdn0141">
              ✦
            </span>
            <span className="sparkle absolute bottom-2 right-[18%] text-brand-purple text-sm md:text-lg" style={{
            animationDelay: '2.2s'
          }} data-id="element-163" data-mp="2fz0wje1774zq">
              ✧
            </span>

            <h1 className="sm:text-4xl md:text-6xl lg:text-7xl text-[28px] font-[700] leading-tight tracking-tight" style={{
            fontFamily: "'Cinzel', serif"
          }} data-id="element-164" data-mp="2p1sbhnta8iw4">
              <span className="headline-shine" data-id="element-165" data-mp="un9ajpoe2ng7">Tratamientos más efectivos</span>
              <br data-id="element-166" data-mp="zlaq40mlw4mx" />
              <span className="phrase-glow font-[800]" data-id="element-167" data-mp="zkraiqkfhsc2">a partir de hoy</span>
              <span className="text-brand-purpleDark" data-id="element-168" data-mp="4b47zvkw08ki">.</span>
            </h1>
          </div>
          <p className="text-[18px] md:text-2xl lg:text-3xl text-brand-amber mb-8 font-bold font-serif" data-id="element-169" data-mp="3vhp1rn2btul8">
            El conocimiento ya lo tienes. El sistema para aplicarlo, ahora
            también.
          </p>

          {/* Hero Image */}
          <div className="relative z-10 w-full max-w-[280px] sm:max-w-sm md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto mb-2 flex justify-center" data-id="element-170" data-mp="9bs6f2bg3ror">
            <div className="relative overflow-hidden rounded-2xl bg-transparent w-full mx-auto" style={{
            aspectRatio: '1/1',
            maxHeight: '500px'
          }} data-id="element-171" data-mp="3cr7fa0837v8b">
              <motion.img src={heroImage.src} alt={heroImage.alt} initial={{
              opacity: 0,
              scale: 0.95
            }} animate={{
              opacity: 1,
              scale: 1
            }} transition={{
              duration: 0.6,
              ease: 'easeOut'
            }} className="absolute inset-0 w-full h-full object-contain mx-auto drop-shadow-2xl" draggable={false} data-id="element-172" data-mp="3ri6lj4lga7oq" />
            </div>
          </div>

          <div className="max-w-3xl mx-auto mb-8 mt-8 space-y-3 px-2" data-id="element-173" data-mp="1e9vquuay86b7">
            <p className="text-[14px] md:text-2xl lg:text-3xl font-bold text-brand-purpleDark font-serif leading-tight" data-id="element-174" data-mp="zkhjtsuui0yz">
              Megapack Saberes Ancestrales Latinoamérica — Sistema Clínico
              Completo
            </p>
            <p className="text-[13px] md:text-xl lg:text-2xl font-bold text-brand-purple font-serif leading-relaxed" data-id="element-175" data-mp="1s8vj0bxo73c4">
              El recurso N°1 para terapeutas que trabajan con plantas
              medicinales en Latinoamérica — Todos los niveles y cuadros
              clínicos
            </p>
            <p className="text-[11px] md:text-base lg:text-lg text-brand-text font-sans" data-id="element-176" data-mp="1n9ag5qfdqau4">
              Enciclopedia A-Z de fitoterapia global + preparaciones
              profesionales + protocolos clínicos + recetario de fórmulas +
              fichas de paciente + espagiria vegetal + fitococina terapéutica.
              Todo listo para usar.
            </p>
          </div>

          <div className="flex flex-nowrap items-center justify-center gap-1.5 md:gap-3 mb-12 text-xs md:text-sm font-medium px-2" data-id="element-177" data-mp="29hxri6p4tooj">
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-178" data-mp="24qg12ckqj6ht">
              <BookOpen className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-179" data-mp="1vs41vm5sz0dr" />
              100% PDF
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-180" data-mp="1qu2mbb7l5j71">
              <Zap className="w-3 h-3 md:w-4 md:h-4 mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-181" data-mp="1gvmsv1l35xud" />
              Acceso en 5 min
            </div>
            <div className="inline-flex items-center bg-white px-2 md:px-3 py-1.5 md:py-2 rounded-full border border-brand-purple/25 whitespace-nowrap shadow-sm text-[9px] md:text-sm" data-id="element-182" data-mp="3b82wrvqeszw2">
              <span className="mr-1 md:mr-1.5 text-brand-purple shrink-0" data-id="element-183" data-mp="3nqf9n4z8ywqz">
                🌿
              </span>
              Todos los niveles
            </div>
          </div>

          <div className="flex flex-col items-center space-y-6" data-id="element-184" data-mp="3sj8ertymo73l">
            <CTAButton variant="purple" href="#pricing" className="text-base md:text-xl px-6 md:px-10 py-4 md:py-5 cta-shimmer relative overflow-hidden" data-id="element-185" data-mp="19j6ab63k6xy">
              <span className="relative z-10" data-id="element-186" data-mp="34rksjpcb9k7n">🌿 → QUIERO MI KIT AHORA</span>
            </CTAButton>

            <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2 text-xs md:text-sm text-brand-muted" data-id="element-187" data-mp="39qxhcfmqt1cv">
              <div className="flex items-center" data-id="element-188" data-mp="3eq0lzyyt95kb">
                <Shield className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-189" data-mp="3ennyz7e7450e" />{' '}
                Garantía 7 días
              </div>
              <div className="flex items-center" data-id="element-190" data-mp="tyqe60rfy8vv">📥 Descarga inmediata</div>
              <div className="flex items-center" data-id="element-191" data-mp="1f3aos2bkp4pw">
                🖨️ Listo para imprimir o usar en tablet
              </div>
              <div className="flex items-center" data-id="element-192" data-mp="1b3089vq5mvws">
                <Lock className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-193" data-mp="1b05n21zbh72b" />{' '}
                Pago seguro
              </div>
              <div className="flex items-center" data-id="element-194" data-mp="1k2ewspmrgwen">
                <CreditCard className="w-3.5 h-3.5 md:w-4 md:h-4 mr-1.5 text-brand-purple" data-id="element-195" data-mp="1k0kcndjbd8bm" />{' '}
                Pago en tu moneda local
              </div>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Social Proof Bar */}
      <div className="mt-12 border-y border-brand-purple/15 bg-white/60 py-4 backdrop-blur-sm" data-id="element-196" data-mp="27sho33iupyc7">
        <div className="md:hidden flex flex-wrap justify-center gap-x-5 gap-y-2 px-4 text-sm pl-[16px] pr-[16px]" data-id="element-197" data-mp="22swoqrzrrnyy">
          <div className="flex items-center" data-id="element-198" data-mp="dezv30zog99s">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-199" data-mp="8f54yznq7nps">+500</span>{' '}
            terapeutas equipados
          </div>
          <div className="flex items-center" data-id="element-200" data-mp="2k279qdjfzcm1">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-201" data-mp="2k38uxau69fhh">5</span>{' '}
            Volúmenes principales
          </div>
          <div className="flex items-center" data-id="element-202" data-mp="2ba5b8ksxo6ee">
            <span className="font-bold text-brand-purple mr-1.5" data-id="element-203" data-mp="2b9dtacs9e76l">3</span> Bonus
            incluidos
          </div>
          <div className="flex items-center" data-id="element-204" data-mp="2f9xohat9v0q3">
            <Star className="w-3.5 h-3.5 text-brand-gold fill-brand-gold mr-1" data-id="element-205" data-mp="21c95k8irm3no" />{' '}
            4.9/5
          </div>
        </div>

        <div className="hidden md:flex flex-row items-center gap-6 justify-center whitespace-nowrap px-4 text-sm lg:text-base" data-id="element-206" data-mp="26bvh8diy3bud">
          <div className="flex items-center" data-id="element-207" data-mp="1sddxss02oli9">
            <span className="font-bold text-brand-purple mr-2" data-id="element-208" data-mp="1wc4ks6atqmrn">+500</span>{' '}
            terapeutas equipados
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-209" data-mp="3mqn6ihkso2zg"></div>
          <div className="flex items-center" data-id="element-210" data-mp="3qpmbu3afu1rf">
            <span className="font-bold text-brand-purple mr-2" data-id="element-211" data-mp="3qokwvnqawv4f">5</span>{' '}
            Volúmenes principales
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-212" data-mp="fv3ph5633bms"></div>
          <div className="flex items-center" data-id="element-213" data-mp="15z5pjjn4zck">
            <span className="font-bold text-brand-purple mr-2" data-id="element-214" data-mp="17aanmotod59">3</span> Bonus
            incluidos
          </div>
          <div className="w-1.5 h-1.5 rounded-full bg-brand-purpleLight/50 shrink-0" data-id="element-215" data-mp="62pk82fsrmvv"></div>
          <div className="flex items-center" data-id="element-216" data-mp="tv61wbvmhcun">
            <Star className="w-4 h-4 text-brand-gold fill-brand-gold mr-1" data-id="element-217" data-mp="ytzhqaydtfxz" />{' '}
            4.9/5 calificación
          </div>
        </div>
      </div>
    </div>;
};